import { useEffect, useState } from 'react';
import { toInventoryCount, toInventoryList } from '../api/inventory-request';
import {
  getPrescriptions,
  getControlledQueue,
  getPreAuthRequests,
  getWardReturns,
} from '../api/pharmacy';
import { listRefunds } from '../api/pharmacyDispense';
import { isActivePrescriptionStatus } from '../utils/pharmacyMappers';
import { PRE_AUTH_TAB_ENABLED } from '../navigation';

const EMPTY = { active: 0, controlled: 0, controlledOrders: 0, preauth: 0, returns: 0, refunds: 0 };

function countActive(payload) {
  return toInventoryList(payload).filter((r) => isActivePrescriptionStatus(r.status)).length;
}

function countAwaiting(payload, status) {
  return toInventoryList(payload).filter((r) => r.status === status).length;
}

function countControlledPending(payload) {
  return toInventoryList(payload).filter((r) => {
    const s = String(r.status || '').toUpperCase();
    return s === 'PENDING' || s === 'AWAITING_AUTHORIZATION';
  }).length;
}

const CLOSED_ORDER_STATUSES = new Set(['FINALIZED', 'CANCELLED']);

/**
 * Distinct still-open orders carrying a controlled line — the counter's view,
 * as opposed to `controlled` which counts individual pending requests for the
 * pharmacist. One order with three controlled lines is one thing to come back to.
 */
function countOpenControlledOrders(payload) {
  const orders = new Set();
  toInventoryList(payload).forEach((r) => {
    const txnId = r.inventory_transaction_id;
    if (!txnId) return;
    if (CLOSED_ORDER_STATUSES.has(String(r.transaction_status || '').toUpperCase())) return;
    orders.add(String(txnId));
  });
  return orders.size;
}

/**
 * Sidebar badge counts for the pharmacy module.
 *
 * `enabled` lets the layout call this unconditionally and only fetch while the user
 * is actually inside pharmacy. There must be exactly one caller — a second one would
 * double every request.
 *
 * Fetched once per entry into the module, not per route: these four counts feed
 * sidebar pills only, and keying the effect on the pathname made every navigation
 * inside pharmacy refire all four — four requests to render a screen that uses none
 * of them. Same reasoning as useOverviewStats. Re-entering pharmacy from another
 * module flips `enabled` and refreshes them.
 */
export default function usePharmacyBadgeCounts(enabled) {
  const [counts, setCounts] = useState(EMPTY);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;

    async function loadCounts() {
      const badgeTasks = [
        { key: 'active', run: () => getPrescriptions({ status: 'ACTIVE' }) },
        // One unfiltered read serves both controlled badges. The pharmacist's
        // pending count and the counter's open-order count are both derived
        // from it below, and the PENDING-only list this used to fetch as well
        // was a strict subset of it — a second request for data already in hand.
        { key: 'controlledQueue', run: () => getControlledQueue({ status: undefined }) },
        ...(PRE_AUTH_TAB_ENABLED
          ? [{ key: 'preauth', run: () => getPreAuthRequests({ status: 'PENDING_INSURANCE' }) }]
          : []),
        { key: 'returns', run: () => getWardReturns({ status: 'SUBMITTED' }) },
        { key: 'refunds', run: () => listRefunds({ status: 'PENDING' }) },
      ];

      const results = await Promise.allSettled(badgeTasks.map((task) => task.run()));

      if (!alive) return;

      const val = (res, fallback = 0) => (res.status === 'fulfilled' ? res.value : fallback);
      const byKey = Object.fromEntries(badgeTasks.map((task, i) => [task.key, results[i]]));
      const controlledQueue = val(byKey.controlledQueue, []);

      setCounts({
        // No `|| toInventoryCount(...)` here: a list that holds rows but none of them
        // active is a genuine zero, and the fallback turned that into the total row
        // count — a badge advertising work that does not exist.
        active: countActive(val(byKey.active, [])),
        // No `|| toInventoryCount(...)` fallback: on an unfiltered list that
        // would count AUTHORIZED rows as pending work, which is precisely the
        // over-counting the note above warns about.
        controlled: countControlledPending(controlledQueue),
        controlledOrders: countOpenControlledOrders(controlledQueue),
        preauth: PRE_AUTH_TAB_ENABLED
          ? countAwaiting(val(byKey.preauth, []), 'PENDING_INSURANCE') || toInventoryCount(val(byKey.preauth, []))
          : 0,
        returns: countAwaiting(val(byKey.returns, []), 'SUBMITTED') || toInventoryCount(val(byKey.returns, [])),
        refunds: toInventoryCount(val(byKey.refunds, [])),
      });
    }

    loadCounts();
    return () => { alive = false; };
  }, [enabled]);

  return enabled ? counts : EMPTY;
}
